import React from "react";
import { History, Trash2, Tag, Download, RotateCw, MapPin, Car } from "lucide-react";
import { FUEL_TYPES, formatCurrency, formatDate } from "./constants";

export default function HistoryPanel({
  history, loadingHistory,
  handleDeleteHistory, handleClearHistory, handleExportCSV,
  user, handleReopenHistory,
}) {
  return (
    <div className="sf-card page-enter delay-200">
      <div className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="sf-subheading flex items-center gap-2"><History className="w-5 h-5 text-[#E52020]" />HISTORY</h2>
          <div className="flex items-center gap-2">
            {history.length > 0 && (
              <>
                <button onClick={handleExportCSV} className="text-sm text-[#E52020] hover:text-[#16A34A] transition-colors px-3 py-1.5 rounded-sm hover:bg-[rgba(229, 32, 32,0.06)] flex items-center gap-1.5 font-mono" data-testid="export-csv-btn" title="Export as CSV">
                  <Download className="w-3.5 h-3.5" />CSV
                </button>
                <button onClick={handleClearHistory} className="text-sm text-[#666666] hover:text-[#DC2626] transition-colors px-3 py-1.5 rounded-sm hover:bg-[rgba(220,38,38,0.06)]" data-testid="clear-history-button">Clear all</button>
              </>
            )}
          </div>
        </div>
        {loadingHistory ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="sf-skeleton h-16 rounded-sm" />)}</div>
        ) : history.length === 0 ? (
          <div className="text-center py-12 text-[#666666]">
            <History className="w-12 h-12 mx-auto mb-3 opacity-20" /><p className="font-['Manrope']">No calculations yet</p><p className="text-sm mt-1 font-mono">Your saved calculations will appear here</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {history.map((item) => (
              <div key={item.id} className="sf-history-item group" data-testid="history-list-item">
                <div className="flex-1 min-w-0">
                  {/* Primary label: From → To route */}
                  {(item.origin_name || item.destination_name) ? (
                    <p className="text-sm font-semibold text-[#1A1A1A] mb-1 flex items-center gap-1.5 font-['Manrope'] truncate" data-testid="history-route-label">
                      <MapPin className="w-3.5 h-3.5 text-[#E52020] flex-shrink-0" />
                      <span className="truncate">{(item.origin_name || '?').split(',')[0]} → {(item.destination_name || '?').split(',')[0]}</span>
                    </p>
                  ) : item.trip_name ? (
                    <p className="text-sm font-semibold text-[#1A1A1A] mb-1 flex items-center gap-1 font-['Manrope']"><Tag className="w-3.5 h-3.5 text-[#E52020]" />{item.trip_name}</p>
                  ) : null}
                  {/* Trip name shown as secondary if route label is present */}
                  {item.trip_name && (item.origin_name || item.destination_name) && (
                    <p className="text-xs text-[#E52020] mb-1 flex items-center gap-1 font-['Manrope']"><Tag className="w-3 h-3" />{item.trip_name}</p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-[#1A1A1A]">{formatCurrency(item.total_cost)}</span>
                    <span className="text-xs text-[#666666] bg-[rgba(229, 32, 32,0.06)] border border-[rgba(229, 32, 32,0.1)] px-2 py-0.5 rounded-sm font-mono">{item.distance_km} km</span>
                    {item.return_trip && <span className="sf-badge-green text-xs">Return</span>}
                    {item.fuel_type && item.fuel_type !== "diesel" && (
                      <span className="text-xs px-2 py-0.5 rounded-sm font-mono font-medium" style={{ color: FUEL_TYPES[item.fuel_type]?.color || '#666666', backgroundColor: `${FUEL_TYPES[item.fuel_type]?.color || '#666666'}10`, border: `1px solid ${FUEL_TYPES[item.fuel_type]?.color || '#666666'}30` }}>
                        {FUEL_TYPES[item.fuel_type]?.label || item.fuel_type}
                      </span>
                    )}
                  </div>
                  {item.vehicle_name && (
                    <p className="text-xs text-[#666666] mt-1 flex items-center gap-1">
                      <Car className="w-3 h-3" />{item.vehicle_name}
                    </p>
                  )}
                  <p className="text-xs text-[#666666] truncate mt-1 font-mono">{item.total_litres}L @ {formatCurrency(item.diesel_price_per_litre)}/L</p>
                  <p className="text-xs text-[#999999] mt-0.5 font-mono">{formatDate(item.created_at)}</p>
                </div>
                <div className="flex items-center gap-1">
                  {/* Re-open button */}
                  <button
                    onClick={() => handleReopenHistory(item)}
                    className="p-2 text-[#E52020] hover:bg-[rgba(229, 32, 32,0.1)] rounded-sm transition-all"
                    data-testid="reopen-history-item"
                    title="Re-open this trip"
                  >
                    <RotateCw className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeleteHistory(item.id)} className="p-2 text-[#D4CEC0] hover:text-[#DC2626] hover:bg-[rgba(220,38,38,0.06)] rounded-sm transition-all" data-testid="delete-history-item">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
